import { useState, useCallback, useRef, useEffect } from 'react'
import { motion, useMotionValue, animate, useMotionValueEvent } from 'framer-motion'
import { Building2, Users, X, Check } from 'lucide-react'
import { useSliderKeyboard } from '@/hooks/useSliderKeyboard'

const rows = [
  {
    criteria: 'Pricing',
    agencies: 'High retainers and project fees',
    inHouse: 'Ongoing salaries, benefits, and overhead',
    gigafish: 'Transparent pricing, no enterprise markup',
  },
  {
    criteria: 'Speed',
    agencies: 'Slow processes, long lead times to start',
    inHouse: 'Recruitment takes months',
    gigafish: 'Agile delivery, start fast and iterate',
  },
  {
    criteria: 'Expertise',
    agencies: 'Junior staff often do the heavy lifting',
    inHouse: 'Limited breadth of expertise',
    gigafish: 'Full-stack expertise, direct access to seniors',
  },
  {
    criteria: 'Approach',
    agencies: 'One-size-fits-all methodologies',
    inHouse: 'Knowledge leaves when they do',
    gigafish: 'Tailored to your goals, not a playbook',
  },
  {
    criteria: 'Flexibility',
    agencies: 'Multiple approval layers, rigid processes',
    inHouse: 'Scaling up or down is costly',
    gigafish: 'Scale when you need to, no long-term lock-in',
  },
]

function CompareRow({ row }: { row: (typeof rows)[0] }) {
  return (
    <>
      <td className="py-5 px-6 md:px-8 font-bold font-heading text-primary text-sm uppercase tracking-wide align-top pt-6">
        {row.criteria}
      </td>
      <td className="py-5 px-6 md:px-8 align-top pt-6">
        <div className="flex items-start gap-3">
          <div className="flex-shrink-0 mt-0.5 w-6 h-6 rounded-full bg-red-100 flex items-center justify-center">
            <X className="w-3.5 h-3.5 text-red-600" strokeWidth={2.5} />
          </div>
          <span className="text-slate-600 text-sm leading-relaxed">{row.agencies}</span>
        </div>
      </td>
      <td className="py-5 px-6 md:px-8 align-top pt-6">
        <div className="flex items-start gap-3">
          <div className="flex-shrink-0 mt-0.5 w-6 h-6 rounded-full bg-amber-100 flex items-center justify-center">
            <X className="w-3.5 h-3.5 text-amber-600" strokeWidth={2.5} />
          </div>
          <span className="text-slate-600 text-sm leading-relaxed">{row.inHouse}</span>
        </div>
      </td>
      <td className="py-5 px-6 md:px-8 align-top pt-6 bg-accent border-l-2 border-accent group-hover:bg-accent/95 transition-colors duration-200">
        <div className="flex items-start gap-3">
          <div className="flex-shrink-0 mt-0.5 w-6 h-6 rounded-full bg-white/20 flex items-center justify-center">
            <Check className="w-3.5 h-3.5 text-white" strokeWidth={2.5} />
          </div>
          <span className="text-white text-sm leading-relaxed font-medium">{row.gigafish}</span>
        </div>
      </td>
    </>
  )
}

function MobileCompareCard({ row }: { row: (typeof rows)[0] }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm w-full flex-shrink-0 min-w-full">
      <h4 className="font-bold font-heading text-primary text-sm uppercase tracking-wide mb-4">
        {row.criteria}
      </h4>
      <div className="space-y-3">
        <div className="flex items-start gap-3">
          <div className="flex-shrink-0 mt-0.5 w-6 h-6 rounded-full bg-red-100 flex items-center justify-center">
            <X className="w-3.5 h-3.5 text-red-600" strokeWidth={2.5} />
          </div>
          <div>
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-0.5">Big Agencies</p>
            <p className="text-slate-600 text-sm leading-relaxed">{row.agencies}</p>
          </div>
        </div>
        <div className="flex items-start gap-3">
          <div className="flex-shrink-0 mt-0.5 w-6 h-6 rounded-full bg-amber-100 flex items-center justify-center">
            <X className="w-3.5 h-3.5 text-amber-600" strokeWidth={2.5} />
          </div>
          <div>
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-0.5">In-House</p>
            <p className="text-slate-600 text-sm leading-relaxed">{row.inHouse}</p>
          </div>
        </div>
        <div className="flex items-start gap-3 rounded-lg bg-accent p-3 -mx-1">
          <div className="flex-shrink-0 mt-0.5 w-6 h-6 rounded-full bg-white/20 flex items-center justify-center">
            <Check className="w-3.5 h-3.5 text-white" strokeWidth={2.5} />
          </div>
          <div>
            <div className="flex items-center gap-2 mb-0.5">
              <img src="/assets/gigafish-wordmark.svg" alt="Gigafish" className="h-4 w-auto brightness-0 invert" />
            </div>
            <p className="text-white text-sm leading-relaxed font-medium">{row.gigafish}</p>
          </div>
        </div>
      </div>
    </div>
  )
}

export default function Compare() {
  const [currentSlide, setCurrentSlide] = useState(0)
  const [displayedSlide, setDisplayedSlide] = useState(0)
  const [slideWidth, setSlideWidth] = useState(0)
  const containerRef = useRef<HTMLDivElement>(null)
  const x = useMotionValue(0)
  const isDraggingRef = useRef(false)

  useMotionValueEvent(x, 'change', (latest) => {
    if (slideWidth > 0) {
      const slide = Math.round(-latest / slideWidth)
      setDisplayedSlide(Math.max(0, Math.min(slide, rows.length - 1)))
    }
  })

  const goToSlide = useCallback((index: number) => {
    setCurrentSlide(Math.max(0, Math.min(index, rows.length - 1)))
  }, [])

  const goPrev = useCallback(() => goToSlide(currentSlide - 1), [currentSlide, goToSlide])
  const goNext = useCallback(() => goToSlide(currentSlide + 1), [currentSlide, goToSlide])

  useEffect(() => {
    const container = containerRef.current
    if (!container) return
    const observer = new ResizeObserver((entries) => {
      const width = entries[0]?.contentRect.width ?? container.offsetWidth
      setSlideWidth(width)
    })
    observer.observe(container)
    setSlideWidth(container.offsetWidth)
    return () => observer.disconnect()
  }, [])

  useEffect(() => {
    if (!isDraggingRef.current && slideWidth > 0) {
      animate(x, -currentSlide * slideWidth, { type: 'spring', stiffness: 300, damping: 30 })
    }
  }, [currentSlide, slideWidth, x])

  const handleDragStart = useCallback(() => {
    isDraggingRef.current = true
  }, [])

  const handleDragEnd = useCallback((_: MouseEvent | TouchEvent | PointerEvent, info: { offset: { x: number }; velocity: { x: number } }) => {
    isDraggingRef.current = false
    if (slideWidth === 0) return
    const threshold = slideWidth * 0.15
    const velocityThreshold = 500
    const shouldGoPrev = info.offset.x > threshold || info.velocity.x > velocityThreshold
    const shouldGoNext = info.offset.x < -threshold || info.velocity.x < -velocityThreshold
    if (shouldGoPrev) goToSlide(currentSlide - 1)
    else if (shouldGoNext) goToSlide(currentSlide + 1)
  }, [currentSlide, goToSlide, slideWidth])

  useSliderKeyboard(containerRef, goPrev, goNext, true)

  return (
    <section id="compare" className="relative py-24 md:py-32 bg-slate-50 border-t border-slate-200 overflow-hidden">
      <div className="container relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12 md:mb-20"
        >
          <h2 className="text-sm font-bold text-accent uppercase tracking-[0.2em] mb-6">Why Gigafish</h2>
          <h3 className="text-3xl md:text-6xl font-bold font-heading text-primary leading-[0.95] uppercase mb-6">
            Not All Fish <br /> <span className="text-accent">Swim the Same.</span>
          </h3>
          <p className="text-slate-600 text-base md:text-lg leading-relaxed max-w-2xl mx-auto px-2">
            Big agencies charge big fees. In-house teams come with big overhead. We deliver enterprise-grade work without the enterprise baggage.
          </p>
        </motion.div>

        {/* Mobile: Carousel slider */}
        <div className="md:hidden">
          <div ref={containerRef} className="relative w-full overflow-hidden">
            <motion.div
              className="flex"
              style={{ x }}
              drag="x"
              dragConstraints={slideWidth > 0 ? { left: -(rows.length - 1) * slideWidth, right: 0 } : undefined}
              dragElastic={0.15}
              onDragStart={handleDragStart}
              onDragEnd={handleDragEnd}
              whileTap={{ cursor: 'grabbing' }}
            >
              {rows.map((row) => (
                <div key={row.criteria} className="flex items-stretch px-2" style={{ minWidth: slideWidth, width: slideWidth }}>
                  <MobileCompareCard row={row} />
                </div>
              ))}
            </motion.div>
          </div>
          <div className="flex items-center justify-center gap-3 mt-6 px-4">
            <button
              onClick={() => goToSlide(currentSlide - 1)}
              disabled={displayedSlide === 0}
              className="flex items-center justify-center w-10 h-10 rounded-full text-accent hover:bg-accent/10 disabled:opacity-30 disabled:cursor-not-allowed transition-all"
              aria-label="Previous"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M15 18l-6-6 6-6" />
              </svg>
            </button>
            <div className="flex items-center gap-1.5">
              {rows.map((_, i) => (
                <button
                  key={i}
                  onClick={() => goToSlide(i)}
                  className={`h-2 rounded-full transition-all duration-300 ${
                    i === displayedSlide ? 'bg-accent w-6' : 'bg-slate-300 w-2 hover:bg-slate-400'
                  }`}
                  aria-label={`Go to slide ${i + 1}`}
                />
              ))}
            </div>
            <button
              onClick={() => goToSlide(currentSlide + 1)}
              disabled={displayedSlide === rows.length - 1}
              className="flex items-center justify-center w-10 h-10 rounded-full text-accent hover:bg-accent/10 disabled:opacity-30 disabled:cursor-not-allowed transition-all"
              aria-label="Next"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M9 18l6-6-6-6" />
              </svg>
            </button>
          </div>
          <p className="text-center text-xs text-slate-400 mt-2">
            {displayedSlide + 1} of {rows.length} · Swipe to compare
          </p>
        </div>

        {/* Desktop: Table layout */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-80px' }}
          transition={{ duration: 0.5 }}
          className="hidden md:block relative"
        >
          <div className="overflow-x-auto rounded-2xl border border-slate-200 bg-white shadow-lg shadow-slate-200/50">
            <table className="w-full min-w-[680px] text-left">
              <thead>
                <tr className="border-b-2 border-slate-200/30 bg-slate-50/80">
                  <th className="py-6 px-6 md:px-8 text-xs font-bold text-slate-500 uppercase tracking-[0.2em] w-[140px] md:w-[160px]">
                    Compare
                  </th>
                  <th className="py-6 px-6 md:px-8">
                    <div className="flex items-center gap-3 text-slate-600">
                      <div className="flex items-center justify-center w-10 h-10 rounded-full bg-slate-200/80">
                        <Building2 className="w-5 h-5" strokeWidth={1.5} />
                      </div>
                      <span className="font-bold font-heading text-sm uppercase tracking-tight">Big Agencies</span>
                    </div>
                  </th>
                  <th className="py-6 px-6 md:px-8">
                    <div className="flex items-center gap-3 text-slate-600">
                      <div className="flex items-center justify-center w-10 h-10 rounded-full bg-slate-200/80">
                        <Users className="w-5 h-5" strokeWidth={1.5} />
                      </div>
                      <span className="font-bold font-heading text-sm uppercase tracking-tight">In-House</span>
                    </div>
                  </th>
                  <th className="py-6 px-6 md:px-8 bg-accent border-l-2 border-accent">
                    <div className="flex items-center gap-3">
                      <img src="/assets/gigafish-brandmark.svg" alt="" className="h-12 w-auto brightness-0 invert" aria-hidden />
                      <img src="/assets/gigafish-wordmark.svg" alt="Gigafish" className="h-5 w-auto brightness-0 invert" />
                    </div>
                  </th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row, i) => (
                  <motion.tr
                    key={row.criteria}
                    initial={{ opacity: 0, x: -12 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true, margin: '-20px' }}
                    transition={{ duration: 0.4, delay: i * 0.05 }}
                    className="group border-b border-slate-200/30 last:border-b-0 transition-colors duration-200 hover:bg-slate-50/70"
                  >
                    <CompareRow row={row} />
                  </motion.tr>
                ))}
              </tbody>
            </table>
          </div>
        </motion.div>
      </div>
    </section>
  )
}
